import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, GripVertical, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const emptyForm = {
  name: "", role_fr: "", role_en: "",
  bio_fr: "", bio_en: "", image_url: "",
  order: 100, published: true,
};

const BoardManager = ({ tAdmin }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/board-members");
      setMembers(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/admin/board-members/${editId}`, form);
        toast.success(tAdmin.board.updated);
      } else {
        await api.post("/admin/board-members", form);
        toast.success(tAdmin.board.created);
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditId(null);
      fetch();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const startEdit = (m) => {
    setEditId(m.id);
    setForm({
      name: m.name, role_fr: m.role_fr, role_en: m.role_en,
      bio_fr: m.bio_fr, bio_en: m.bio_en,
      image_url: m.image_url || "", order: m.order || 100, published: m.published,
    });
    setShowForm(true);
  };

  const remove = async (m) => {
    if (!window.confirm(tAdmin.board.confirmDelete.replace("{name}", m.name))) return;
    try {
      await api.delete(`/admin/board-members/${m.id}`);
      toast.success(tAdmin.board.deleted);
      fetch();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const togglePublish = async (m) => {
    try {
      await api.put(`/admin/board-members/${m.id}`, { published: !m.published });
      fetch();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6" data-testid="board-manager">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[#0A192F]">{tAdmin.board.title}</h2>
          <p className="text-sm text-[#64748B] mt-1">{tAdmin.board.subtitle}</p>
        </div>
        <button
          onClick={() => { setShowForm((s) => !s); setEditId(null); setForm(emptyForm); }}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium"
          data-testid="board-add-btn"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? tAdmin.board.cancel : tAdmin.board.addMember}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-[#FDFBF7] p-5 rounded-xl border border-black/5" data-testid="board-form">
          <Field label={tAdmin.board.fName} testid="board-form-name">
            <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="form-input" data-testid="board-form-name-input" />
          </Field>
          <Field label={tAdmin.board.fImage}>
            <input type="url" placeholder="https://…" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="form-input" data-testid="board-form-image-input" />
          </Field>
          <Field label={tAdmin.board.fRoleFr}>
            <input type="text" required value={form.role_fr} onChange={(e) => setForm({ ...form, role_fr: e.target.value })}
              className="form-input" />
          </Field>
          <Field label={tAdmin.board.fRoleEn}>
            <input type="text" required value={form.role_en} onChange={(e) => setForm({ ...form, role_en: e.target.value })}
              className="form-input" />
          </Field>
          <Field label={tAdmin.board.fBioFr} className="md:col-span-2">
            <textarea required minLength={10} maxLength={600} rows={3} value={form.bio_fr}
              onChange={(e) => setForm({ ...form, bio_fr: e.target.value })}
              className="form-input" />
          </Field>
          <Field label={tAdmin.board.fBioEn} className="md:col-span-2">
            <textarea required minLength={10} maxLength={600} rows={3} value={form.bio_en}
              onChange={(e) => setForm({ ...form, bio_en: e.target.value })}
              className="form-input" />
          </Field>
          <Field label={tAdmin.board.fOrder}>
            <input type="number" min="0" max="999" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
              className="form-input" />
          </Field>
          <Field label={tAdmin.board.fPublished}>
            <select value={form.published ? "1" : "0"} onChange={(e) => setForm({ ...form, published: e.target.value === "1" })}
              className="form-input bg-white">
              <option value="1">{tAdmin.board.yes}</option>
              <option value="0">{tAdmin.board.no}</option>
            </select>
          </Field>
          <div className="md:col-span-2">
            <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#C5A059] text-white hover:bg-[#B08D4A] text-sm font-medium" data-testid="board-form-submit">
              <Save size={14} /> {editId ? tAdmin.board.save : tAdmin.board.create}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="p-8 text-center text-[#64748B]">…</div>
      ) : (
        <div className="space-y-3">
          {members.length === 0 ? (
            <div className="p-12 text-center text-[#64748B]">{tAdmin.board.empty}</div>
          ) : (
            members.map((m, i) => (
              <div key={m.id} className="flex items-center gap-4 bg-[#FDFBF7] p-4 rounded-xl border border-black/5" data-testid={`board-row-${i}`}>
                <GripVertical size={14} className="text-[#94A3B8] shrink-0" />
                {m.image_url && <img src={m.image_url} alt={m.name} className="w-12 h-12 rounded-full object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0A192F] truncate">{m.name}</div>
                  <div className="text-xs text-[#C5A059]">{m.role_fr}</div>
                </div>
                <span className="text-[10px] uppercase text-[#64748B]">#{m.order}</span>
                <button onClick={() => togglePublish(m)} title={m.published ? "Dépublier" : "Publier"}
                  className={m.published ? "text-emerald-600" : "text-[#94A3B8]"} data-testid={`board-toggle-${i}`}>
                  {m.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => startEdit(m)} className="text-[#002B49] hover:text-[#C5A059] text-xs font-medium" data-testid={`board-edit-${i}`}>
                  {tAdmin.board.edit}
                </button>
                <button onClick={() => remove(m)} className="text-red-600 hover:text-red-800" data-testid={`board-delete-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      <style>{`.form-input { width:100%; padding:0.5rem 0.75rem; border-radius:0.375rem; border:1px solid rgba(0,0,0,0.1); outline:none; font-size:0.875rem; }
.form-input:focus { border-color:#C5A059; }`}</style>
    </div>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-[#64748B] uppercase tracking-wider mb-1.5">{label}</label>
    {children}
  </div>
);

export default BoardManager;
